import { PageLoader } from '@/components/ui/loader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowLeft, ArrowUp, Copy, ExternalLink, Globe, ImagePlus, LayoutTemplate, Monitor, Plus, Smartphone, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ApiRequestError, apiGet } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { SeoPanel } from '@/features/sales/extras-pages';
import { PageBlocks } from './page-blocks';
import {
  useForms,
  usePage,
  usePageActions,
  usePages,
  usePublishPage,
  useSavePage,
  useUploadAsset,
  type Block,
  type BlockType,
  type PageTheme,
  type PublicForm,
} from './api';

let counter = 0;
const newId = () => `b${Date.now().toString(36).slice(-4)}${(counter += 1)}`;

const BLOCKS: { type: BlockType; label: string; make: () => Block }[] = [
  { type: 'hero', label: 'Hero', make: () => ({ id: newId(), type: 'hero', eyebrow: '', heading: 'A headline that says what you do', subheading: 'One or two lines on why it matters to them.', buttonLabel: 'Get a free quote', buttonUrl: '#form', imageUrl: '', align: 'center' }) },
  { type: 'text', label: 'Text', make: () => ({ id: newId(), type: 'text', heading: 'About us', body: 'Write here. **Bold**, *italic*, [links](https://example.com) and lists all work.' }) },
  { type: 'image', label: 'Image', make: () => ({ id: newId(), type: 'image', url: '', alt: '', caption: '' }) },
  {
    type: 'features',
    label: 'Features',
    make: () => ({
      id: newId(),
      type: 'features',
      heading: 'Why choose us',
      items: [
        { title: 'Fast', body: 'Done in days, not weeks.' },
        { title: 'Licensed', body: 'Fully licensed in the UAE.' },
        { title: 'Supported', body: 'A real person on WhatsApp, 7 days a week.' },
      ],
    }),
  },
  { type: 'form', label: 'Form', make: () => ({ id: newId(), type: 'form', heading: 'Get your free quote', body: 'We reply within the hour.', formKey: '' }) },
  { type: 'stats', label: 'Numbers', make: () => ({ id: newId(), type: 'stats', items: [{ value: '500+', label: 'Happy clients' }, { value: '12', label: 'Years in Dubai' }, { value: '4.9★', label: 'Google rating' }, { value: '24h', label: 'Response time' }] }) },
  {
    type: 'testimonials',
    label: 'Testimonials',
    make: () => ({ id: newId(), type: 'testimonials', heading: 'What clients say', items: [{ quote: 'Professional from start to finish.', name: 'Aisha K.', role: 'Dubai Marina' }] }),
  },
  { type: 'faq', label: 'FAQ', make: () => ({ id: newId(), type: 'faq', heading: 'Questions', items: [{ q: 'How long does it take?', a: 'Usually two weeks.' }] }) },
  { type: 'cta', label: 'Call to action', make: () => ({ id: newId(), type: 'cta', heading: 'Ready to start?', body: 'Book a free consultation today.', buttonLabel: 'Book now', buttonUrl: '#form' }) },
  { type: 'logos', label: 'Logos', make: () => ({ id: newId(), type: 'logos', heading: 'Trusted by', images: [] }) },
  { type: 'video', label: 'Video', make: () => ({ id: newId(), type: 'video', url: '' }) },
];

function starter(): Block[] {
  return ['hero', 'stats', 'features', 'form', 'testimonials', 'faq', 'cta'].map((type) => BLOCKS.find((block) => block.type === type)!.make());
}

/** Landing pages (HubSpot). */
export function PagesPage() {
  const navigate = useNavigate();
  const { data: pages = [], isLoading } = usePages();
  const save = useSavePage();
  const { duplicate, remove } = usePageActions();

  const create = () =>
    save.mutate(
      { title: 'New landing page', blocks: starter(), theme: { primary: '#2563eb', background: 'aurora' } },
      { onSuccess: (page) => navigate(`/marketing/pages/${page.id}`), onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not create') },
    );

  return (
    <div>
      <PageHeader
        title="Landing pages"
        description="Campaign pages built from blocks, with your form on them. Published at dovocrm.com/lp/your-page — views and conversions counted."
        breadcrumbs={[{ label: 'Marketing hub' }, { label: 'Landing pages' }]}
        actions={
          <Can permission="marketing.page.create">
            <Button onClick={create} disabled={save.isPending}>
              <Plus /> New page
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : pages.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <LayoutTemplate className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No landing pages yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Start from the lead-generation layout: hero, numbers, features, form, testimonials, FAQ.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pages.map((page) => (
            <Card key={page.id}>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/marketing/pages/${page.id}`} className="block truncate font-semibold hover:underline">
                      {page.title}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">/lp/{page.slug} · edited {timeAgo(page.updatedAt)}</p>
                  </div>
                  <Badge className={page.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>
                    {page.status === 'PUBLISHED' ? 'Live' : 'Draft'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {(
                    [
                      ['Views', page.viewCount],
                      ['Leads', page.conversions],
                      ['Converts', `${page.conversionRate}%`],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-muted/40 py-2">
                      <p className="text-base font-bold">{value}</p>
                      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/marketing/pages/${page.id}`}>Edit</Link>
                  </Button>
                  {page.status === 'PUBLISHED' && (
                    <Button size="sm" variant="ghost" asChild>
                      <a href={page.url} target="_blank" rel="noreferrer">
                        <ExternalLink /> Open
                      </a>
                    </Button>
                  )}
                  <Can permission="marketing.page.create">
                    <Button size="sm" variant="ghost" onClick={() => duplicate.mutate(page.id)}>
                      <Copy /> Copy
                    </Button>
                  </Can>
                  <Can permission="marketing.page.delete">
                    <Button size="sm" variant="ghost" onClick={() => window.confirm(`Delete “${page.title}”?`) && remove.mutate(page.id)}>
                      <Trash2 />
                    </Button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── The editor ───────────────────────────────────────────────────────────────

export function PageEditorPage() {
  const { pageId } = useParams();
  const { data: page, isLoading } = usePage(pageId);
  const { data: forms = [] } = useForms();
  const save = useSavePage();
  const queryClient = useQueryClient();
  const publish = usePublishPage();
  const upload = useUploadAsset();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [theme, setTheme] = useState<PageTheme>({ primary: '#2563eb', background: 'light' });
  const [open, setOpen] = useState<string | null>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [dirty, setDirty] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<(url: string) => void>(() => undefined);

  useEffect(() => {
    if (!page) return;
    setTitle(page.title);
    setSlug(page.slug);
    setDescription(page.description ?? '');
    setBlocks(page.blocks);
    setTheme(page.theme ?? { primary: '#2563eb', background: 'light' });
    setDirty(false);
  }, [page]);

  const formKeys = useMemo(() => [...new Set(blocks.filter((block) => block.type === 'form' && block.formKey).map((block) => String(block.formKey)))], [blocks]);
  const formQueries = useQueries({
    queries: formKeys.map((key) => ({
      queryKey: ['public-form', key, 'preview'],
      queryFn: () => apiGet<PublicForm>(`/public/forms/${key}`, { params: { count: '0' } }),
      staleTime: 30_000,
    })),
  });
  const previewForms = Object.fromEntries(formKeys.map((key, index) => [key, formQueries[index]?.data ?? null]));

  const change = (next: Block[]) => {
    setBlocks(next);
    setDirty(true);
  };
  const update = (id: string, patch: Record<string, unknown>) => change(blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)));
  const move = (index: number, by: number) => {
    const next = [...blocks];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item);
    change(next);
  };
  const pickImage = (apply: (url: string) => void) => {
    uploadTarget.current = apply;
    fileInput.current?.click();
  };

  const persist = async () => {
    if (!page) return;
    try {
      await save.mutateAsync({ id: page.id, title, slug, description: description || null, blocks, theme });
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['seo', 'page', page.id] });
      toast.success('Saved');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Could not save');
    }
  };

  if (isLoading || !page) return <PageLoader />;

  return (
    <div className="flex h-full flex-col">
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          upload.mutate(file, {
            onSuccess: (asset) => uploadTarget.current(asset.url),
            onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Upload failed'),
          });
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-card px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Button size="sm" variant="ghost" asChild>
            <Link to="/marketing/pages">
              <ArrowLeft />
            </Link>
          </Button>
          <p className="truncate font-semibold">{title}</p>
          <Badge className={page.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>{page.status === 'PUBLISHED' ? 'Live' : 'Draft'}</Badge>
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="hidden rounded-lg border p-0.5 sm:flex">
            {(
              [
                ['desktop', Monitor],
                ['mobile', Smartphone],
              ] as const
            ).map(([value, Icon]) => (
              <button key={value} onClick={() => setDevice(value)} className={cn('rounded-md px-2 py-1', device === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={persist} disabled={save.isPending || !dirty}>
            Save
          </Button>
          {page.status === 'PUBLISHED' ? (
            <>
              <Button size="sm" variant="ghost" asChild>
                <a href={page.url} target="_blank" rel="noreferrer">
                  <ExternalLink />
                </a>
              </Button>
              <Button size="sm" variant="outline" onClick={() => publish.mutate({ id: page.id, published: false })}>
                Unpublish
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={async () => {
                if (dirty) await persist();
                publish.mutate({ id: page.id, published: true }, { onSuccess: () => toast.success('Published') });
              }}
            >
              <Globe /> Publish
            </Button>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-e bg-card p-4 lg:w-[23rem]">
          <div className="space-y-2">
            <Label>Page title</Label>
            <Input value={title} onChange={(event) => { setTitle(event.target.value); setDirty(true); }} />
            <Label>Address</Label>
            <div className="flex items-center rounded-lg border ps-2 text-sm">
              <span className="text-muted-foreground">/lp/</span>
              <input className="h-9 min-w-0 flex-1 bg-transparent px-1 outline-none" value={slug} onChange={(event) => { setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')); setDirty(true); }} />
            </div>
            <Label>Search description</Label>
            <Textarea rows={2} value={description} onChange={(event) => { setDescription(event.target.value); setDirty(true); }} />
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Background</Label>
                <Select value={theme.background} onChange={(event) => { setTheme({ ...theme, background: event.target.value as PageTheme['background'] }); setDirty(true); }}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="aurora">Aurora glow</option>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Colour</Label>
                <input type="color" className="h-10 w-full rounded-lg border" value={theme.primary} onChange={(event) => { setTheme({ ...theme, primary: event.target.value }); setDirty(true); }} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blocks</p>
            {blocks.map((block, index) => (
              <div key={block.id} className="rounded-xl border">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button className="min-w-0 flex-1 truncate text-start text-sm font-medium" onClick={() => setOpen(open === block.id ? null : block.id)}>
                    {BLOCKS.find((item) => item.type === block.type)?.label}
                    <span className="ms-1 font-normal text-muted-foreground">{String(block.heading ?? '').slice(0, 28)}</span>
                  </button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={index === 0} onClick={() => move(index, -1)}>
                    <ArrowUp />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={index === blocks.length - 1} onClick={() => move(index, 1)}>
                    <ArrowDown />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => change(blocks.filter((item) => item.id !== block.id))}>
                    <Trash2 />
                  </Button>
                </div>
                {open === block.id && (
                  <div className="space-y-2 border-t p-3">
                    <BlockFields block={block} forms={forms.map((form) => ({ key: form.publicKey, name: form.name }))} onChange={(patch) => update(block.id, patch)} onPickImage={pickImage} />
                  </div>
                )}
              </div>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full">
                  <Plus /> Add block
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {BLOCKS.map((item) => (
                  <DropdownMenuItem
                    key={item.type}
                    onClick={() => {
                      const block = item.make();
                      change([...blocks, block]);
                      setOpen(block.id);
                    }}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <SeoPanel kind="page" id={page.id} />
        </aside>
        <main className="min-h-0 flex-1 overflow-y-auto bg-muted/40 p-4">
          <div className={cn('mx-auto overflow-hidden rounded-2xl border bg-white shadow-xl transition-all', device === 'mobile' ? 'max-w-[400px]' : 'max-w-6xl')}>
            <PageBlocks blocks={blocks} theme={theme} forms={previewForms} preview />
          </div>
        </main>
      </div>
    </div>
  );
}

type Patch = Record<string, unknown>;

function ImageField({ label, value, onChange, onPickImage }: { label: string; value: string; onChange: (url: string) => void; onPickImage: (apply: (url: string) => void) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1.5">
        <Input className="h-8 text-xs" value={value} onChange={(event) => onChange(event.target.value)} placeholder="https://…" />
        <Button size="sm" variant="outline" className="h-8" onClick={() => onPickImage(onChange)}>
          <ImagePlus />
        </Button>
      </div>
    </div>
  );
}

function BlockFields({
  block,
  forms,
  onChange,
  onPickImage,
}: {
  block: Block;
  forms: { key: string; name: string }[];
  onChange: (patch: Patch) => void;
  onPickImage: (apply: (url: string) => void) => void;
}) {
  const value = (key: string) => String(block[key] ?? '');
  const field = (key: string, label: string, long = false) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {long ? (
        <Textarea rows={4} className="text-sm" value={value(key)} onChange={(event) => onChange({ [key]: event.target.value })} />
      ) : (
        <Input className="h-8 text-sm" value={value(key)} onChange={(event) => onChange({ [key]: event.target.value })} />
      )}
    </div>
  );
  const list = (Array.isArray(block.items) ? block.items : []) as Record<string, string>[];
  const listEditor = (keys: [string, string, boolean?][], blank: Record<string, string>) => (
    <div className="space-y-2">
      {list.map((item, index) => (
        <div key={index} className="space-y-1 rounded-lg bg-muted/40 p-2">
          {keys.map(([key, label, long]) =>
            long ? (
              <Textarea
                key={key}
                rows={2}
                className="text-xs"
                placeholder={label}
                value={item[key] ?? ''}
                onChange={(event) => onChange({ items: list.map((entry, at) => (at === index ? { ...entry, [key]: event.target.value } : entry)) })}
              />
            ) : (
              <Input
                key={key}
                className="h-8 text-xs"
                placeholder={label}
                value={item[key] ?? ''}
                onChange={(event) => onChange({ items: list.map((entry, at) => (at === index ? { ...entry, [key]: event.target.value } : entry)) })}
              />
            ),
          )}
          <button className="text-xs text-destructive" onClick={() => onChange({ items: list.filter((_, at) => at !== index) })}>
            Remove
          </button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ items: [...list, blank] })}>
        <Plus /> Add
      </Button>
    </div>
  );

  switch (block.type) {
    case 'hero':
      return (
        <>
          {field('eyebrow', 'Small text above')}
          {field('heading', 'Headline', true)}
          {field('subheading', 'Text', true)}
          {field('buttonLabel', 'Button')}
          {field('buttonUrl', 'Button goes to (#form for the form)')}
          <ImageField label="Image" value={value('imageUrl')} onChange={(url) => onChange({ imageUrl: url })} onPickImage={onPickImage} />
          <Select className="h-8 text-xs" value={value('align') || 'center'} onChange={(event) => onChange({ align: event.target.value })}>
            <option value="center">Centred</option>
            <option value="left">Text left, image right</option>
          </Select>
        </>
      );
    case 'text':
      return (
        <>
          {field('heading', 'Heading')}
          {field('body', 'Text (Markdown)', true)}
        </>
      );
    case 'image':
      return (
        <>
          <ImageField label="Image" value={value('url')} onChange={(url) => onChange({ url })} onPickImage={onPickImage} />
          {field('alt', 'Describe the image')}
          {field('caption', 'Caption')}
        </>
      );
    case 'features':
      return (
        <>
          {field('heading', 'Heading')}
          {listEditor([['title', 'Title'], ['body', 'Text', true]], { title: '', body: '' })}
        </>
      );
    case 'form':
      return (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Form</Label>
            <Select className="h-8 text-sm" value={value('formKey')} onChange={(event) => onChange({ formKey: event.target.value })}>
              <option value="">Choose a form…</option>
              {forms.map((form) => (
                <option key={form.key} value={form.key}>
                  {form.name}
                </option>
              ))}
            </Select>
            {!forms.length && (
              <Link to="/marketing/forms" className="text-xs text-primary underline">
                Create a form first
              </Link>
            )}
          </div>
          {field('heading', 'Heading')}
          {field('body', 'Text beside the form', true)}
        </>
      );
    case 'cta':
      return (
        <>
          {field('heading', 'Heading')}
          {field('body', 'Text', true)}
          {field('buttonLabel', 'Button')}
          {field('buttonUrl', 'Button goes to')}
        </>
      );
    case 'testimonials':
      return (
        <>
          {field('heading', 'Heading')}
          {listEditor([['quote', 'What they said', true], ['name', 'Name'], ['role', 'Company or area']], { quote: '', name: '', role: '' })}
        </>
      );
    case 'faq':
      return (
        <>
          {field('heading', 'Heading')}
          {listEditor([['q', 'Question'], ['a', 'Answer', true]], { q: '', a: '' })}
        </>
      );
    case 'stats':
      return listEditor([['value', 'Number'], ['label', 'Label']], { value: '', label: '' });
    case 'logos': {
      const images = (Array.isArray(block.images) ? block.images : []) as string[];
      return (
        <>
          {field('heading', 'Heading')}
          {images.map((src, index) => (
            <ImageField
              key={index}
              label={`Logo ${index + 1}`}
              value={src}
              onChange={(url) => onChange({ images: images.map((item, at) => (at === index ? url : item)) })}
              onPickImage={onPickImage}
            />
          ))}
          <Button size="sm" variant="outline" onClick={() => onChange({ images: [...images, ''] })}>
            <Plus /> Add logo
          </Button>
        </>
      );
    }
    case 'video':
      return field('url', 'YouTube or Vimeo link');
    default:
      return null;
  }
}
